import { useDispatch, useSelector } from "react-redux";
import {
  periodChanged,
  customPeriodSet,
  compareChanged,
  customCompareSet,
  selectDashboardFilter,
} from "./dashboardFilterSlice";
import dayjs from "dayjs";
import { resolvePeriod, resolveComparePeriod, getAvailableCompareOptions, PERIOD_OPTIONS } from "./periodUtils";
import { useResolvedYesterday } from "../masters/holidays/useWorkingDay";

// The one hook every comparison-capable widget calls - same "read straight
// from a global slice, no prop drilling" shape as useBranchFilter(). A
// widget that isn't comparison-capable simply never imports this, exactly
// like a snapshot widget never imports useBranchFilter's date-range
// equivalent today.
export const useDashboardPeriod = () => {
  const dispatch = useDispatch();
  const { periodKey, customPeriodFrom, customPeriodTo, compareKey, customCompareFrom, customCompareTo } =
    useSelector(selectDashboardFilter);

  const rawPeriod = resolvePeriod(periodKey, customPeriodFrom, customPeriodTo);
  const compareOptions = getAvailableCompareOptions(periodKey);
  // A compareKey left over from a different period's menu (e.g. switching
  // Period from "This Month" to "Today" while Compare was "Multi-Year")
  // simply won't resolve to anything meaningful for the new window - guard
  // by falling back to the new period's first available option rather than
  // resolving a nonsensical comparison silently.
  const effectiveCompareKey = compareOptions.some((o) => o.value === compareKey)
    ? compareKey
    : compareOptions[0]?.value;
  // "Yesterday" means the last working day (2026-09-14) - on a Monday the Period
  // used to resolve to Sunday and every widget showed nothing. Resolved here, once,
  // so no widget does its own holiday lookup. Covers both places the dashboard says
  // "yesterday": the Period itself, and Today's "compare with Yesterday".
  const needsYesterday = periodKey === "YESTERDAY" || effectiveCompareKey === "YESTERDAY";
  const yesterday = useResolvedYesterday({ skip: !needsYesterday });
  const period =
    periodKey === "YESTERDAY" && yesterday.shifted
      ? { ...rawPeriod, dateFrom: yesterday.date, dateTo: yesterday.date, note: yesterday.note }
      : rawPeriod;

  let comparison = effectiveCompareKey
    ? resolveComparePeriod(period.dateFrom, period.dateTo, effectiveCompareKey, customCompareFrom, customCompareTo)
    : null;
  if (effectiveCompareKey === "YESTERDAY" && yesterday.shifted && comparison?.type === "single") {
    comparison = {
      ...comparison,
      dateFrom: yesterday.date,
      dateTo: yesterday.date,
      label: `Last working day (${dayjs(yesterday.date).format("ddd D MMM")})`,
    };
  }

  return {
    periodKey,
    period, // { dateFrom, dateTo, label, note? } - note set when "Yesterday" was moved off a holiday/Sunday
    setPeriod: (key) => dispatch(periodChanged(key)),
    customPeriodFrom,
    customPeriodTo,
    setCustomPeriod: (dateFrom, dateTo) => dispatch(customPeriodSet({ dateFrom, dateTo })),
    periodOptions: PERIOD_OPTIONS,

    compareKey: effectiveCompareKey,
    compareOptions,
    setCompare: (key) => dispatch(compareChanged(key)),
    customCompareFrom,
    customCompareTo,
    setCustomCompare: (dateFrom, dateTo) => dispatch(customCompareSet({ dateFrom, dateTo })),
    comparison, // null | { type: "single", dateFrom, dateTo, label } | { type: "multi", windows, label }
  };
};
